// ═══════════════════════════════════════════════════════════
// FOIA — Document Records API
//
// Operations on the 'documents' GAB table.
// For actual file upload/download, see documents.ts.
//
// A "document record" in FOIA is a row in the documents table
// that tracks metadata about a file. The actual file bytes live
// in GAB document storage, referenced by the storageUrl field
// (uses |||DOCID|||{uuid}||| marker — see documents.ts).
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord, fetchRecord, deleteRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { uploadDocument, buildStorageUrl, getDownloadUrlFromField, cacheFileForDoc } from './documents';

const F = FIELDS.documents;

// ── Domain types ─────────────────────────────────────────

export type ResponsiveClass = 'responsive' | 'nonResponsive' | 'partiallyResponsive' | 'unclassified';
export type RedactionStatus = 'none' | 'pending' | 'inProgress' | 'complete' | 'approved';
export type DocumentSource = 'requester' | 'staff' | 'department' | 'upload-link';

export interface FoiaDocument {
  recordId: number;
  requestId: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storageUrl: string;               // contains |||DOCID|||{uuid}||| marker
  redactedStorageUrl?: string;      // same format, for the redacted copy
  uploadedBy: string;
  uploadedAt: string;
  source: DocumentSource;
  isResponsive?: boolean;           // quick boolean flag
  responsiveClass: ResponsiveClass;
  redactionStatus: RedactionStatus;
  exemptionCodes?: string;          // JSON array of exemption code IDs
  isReleased: boolean;
  releasedAt?: string;
  isPublished: boolean;       // true when [PUBLISHED] marker is in notes
  isStandalone: boolean;      // true when [STANDALONE] marker is in notes (redaction-only, no FOIA request)
  // AI classification
  aiResponsive?: boolean;
  aiConfidence?: number;
  notes?: string;
}

// ── Row mapper ───────────────────────────────────────────

function rowToDoc(row: Record<string, unknown>): FoiaDocument {
  return {
    recordId:          row['record_id'] as number,
    requestId:         row[F.requestId] as number,
    fileName:          String(row[F.fileName] ?? ''),
    fileSize:          Number(row[F.fileSize] ?? 0),
    mimeType:          String(row[F.mimeType] ?? ''),
    storageUrl:        String(row[F.storageUrl] ?? ''),
    redactedStorageUrl: row[F.redactedStorageUrl] as string | undefined,
    uploadedBy:        String(row[F.uploadedBy] ?? ''),
    uploadedAt:        String(row[F.uploadedAt] ?? ''),
    source:            (row[F.source] as DocumentSource) ?? 'staff',
    isResponsive:      row[F.isResponsive] === true || row[F.isResponsive] === 'true' ? true
                       : row[F.isResponsive] === false || row[F.isResponsive] === 'false' ? false
                       : undefined,
    responsiveClass:   (row[F.responsiveClass] as ResponsiveClass) ?? 'unclassified',
    redactionStatus:   (row[F.redactionStatus] as RedactionStatus) ?? 'none',
    exemptionCodes:    row[F.exemptionCodes] as string | undefined,
    isReleased:        row[F.isReleased] === true || row[F.isReleased] === 'true',
    releasedAt:        row[F.releasedAt] as string | undefined,
    aiResponsive:      row[F.aiResponsive] === true || row[F.aiResponsive] === 'true' ? true : undefined,
    aiConfidence:      row[F.aiConfidence] as number | undefined,
    isPublished:       String(row[F.notes] ?? '').includes('[PUBLISHED]'),
    isStandalone:      String(row[F.notes] ?? '').includes('[STANDALONE]'),
    notes:             String(row[F.notes] ?? '').replace('[PUBLISHED]', '').replace('[STANDALONE]', '').trim() || undefined,
  };
}

// ── Queries ──────────────────────────────────────────────

export async function getDocument(recordId: number): Promise<FoiaDocument | null> {
  const row = await fetchRecord<Record<string, unknown>>(TABLES.documents, recordId);
  return row ? rowToDoc(row) : null;
}

export async function getDocumentsForRequest(requestId: number, opts?: { anonymous?: boolean }): Promise<FoiaDocument[]> {
  const { records } = await queryRecords(TABLES.documents, {
    filters: [{ field: F.requestId, operator: 'eq', value: requestId }],
    sort: { field: F.uploadedAt, direction: 'desc' },
    limit: 200,
  }, opts);
  return (records as Record<string, unknown>[]).map(rowToDoc);
}

/**
 * Get standalone (non-FOIA) documents for a specific staff member.
 * These are documents uploaded directly to the Redaction tool with no associated request.
 */
export async function getStandaloneDocuments(uploadedByEmail: string): Promise<FoiaDocument[]> {
  const { records } = await queryRecords(TABLES.documents, {
    filters: [{ field: F.notes, operator: 'contains', value: '[STANDALONE]' }],
    sort: { field: F.uploadedAt, direction: 'desc' },
    limit: 500,
  });
  return (records as Record<string, unknown>[])
    .map(rowToDoc)
    .filter(d => d.uploadedBy === uploadedByEmail);
}

export async function getReleasedDocuments(requestId: number, opts?: { anonymous?: boolean }): Promise<FoiaDocument[]> {
  const docs = await getDocumentsForRequest(requestId, opts);
  return docs.filter(d => d.isReleased);
}

// ── Upload + record creation ─────────────────────────────

export interface UploadDocumentInput {
  requestId?: number; // omit for standalone (non-FOIA) redactions
  file: File;
  source: DocumentSource;
  uploadedByEmail: string;
  uploaderName?: string;   // for upload-link: display name of the person who uploaded
  notes?: string;
  anonymous?: boolean;
  onProgress?: (pct: number) => void;
}

/**
 * Upload a file to GAB storage and create a document record.
 *
 * This combines two operations:
 *   1. POST file to GAB documents endpoint → get docId
 *   2. CREATE record in documents table with storageUrl = |||DOCID|||{docId}|||
 *
 * Returns the new FoiaDocument record.
 */
export async function uploadAndCreateDocument(input: UploadDocumentInput): Promise<FoiaDocument> {
  // Step 1: Upload file bytes to GAB storage
  const docRef = await uploadDocument(input.file, {
    anonymous: input.anonymous,
    onProgress: input.onProgress,
    tableKey: TABLES.documents,
  });

  // Cache the file locally so the Redaction Editor can load it immediately
  // without needing a GAB download-url (which requires ATTACHMENT fields).
  cacheFileForDoc(docRef.id, input.file);

  // Step 2: Create the document record with the GAB doc ID
  const storageUrl = buildStorageUrl(docRef.id);
  const now = new Date().toISOString();

  const row = await createRecord(TABLES.documents, {
    ...(input.requestId != null ? { [F.requestId]: input.requestId } : {}),
    [F.fileName]:             docRef.filename ?? input.file.name,
    [F.fileSize]:             docRef.size ?? input.file.size,
    [F.mimeType]:             input.file.type || 'application/octet-stream',
    [F.storageUrl]:           storageUrl,
    [F.uploadedBy]:           input.uploaderName ?? input.uploadedByEmail,
    [F.uploadedAt]:           now,
    [F.source]:               input.source,
    [F.responsiveClass]:      'unclassified',
    [F.redactionStatus]:      'none',
    [F.isReleased]:           false,
    [F.notes]:                input.notes ?? '',
  }, { anonymous: input.anonymous });

  // Write attachmentId as a separate update AFTER record creation.
  // GAB processes ATTACHMENT field writes as a distinct event that confirms
  // the S3 upload — this must happen after createRecord so GAB can link
  // the attachment to the record and make download-url work in any session.
  const created = rowToDoc(row as Record<string, unknown>);
  if (docRef.attachmentId) {
    await updateRecord(TABLES.documents, created.recordId, {
      [F.fileAttachmentId]: docRef.attachmentId,
    }, { anonymous: input.anonymous });
  }

  return created;
}

/**
 * Upload the redacted version of an existing document.
 * Stores the redacted file's docId in redactedStorageUrl.
 * Does NOT mark the document as released — that requires a separate
 * approval step.
 */
export async function uploadRedactedVersion(
  docRecordId: number,
  redactedFile: File,
  uploadedByEmail: string,
  onProgress?: (pct: number) => void,
): Promise<FoiaDocument> {
  const docRef = await uploadDocument(redactedFile, { onProgress, tableKey: TABLES.documents });
  const redactedUrl = buildStorageUrl(docRef.id);

  const updated = await updateRecord(TABLES.documents, docRecordId, {
    [F.redactedStorageUrl]: redactedUrl,
    [F.redactionStatus]:    'complete',
  });

  // Write attachmentId as a separate update — same reason as uploadAndCreateDocument:
  // GAB needs this as a distinct event to confirm the S3 upload for download-url.
  if (docRef.attachmentId) {
    await updateRecord(TABLES.documents, docRecordId, {
      [F.redactedFileAttachmentId]: docRef.attachmentId,
    });
  }

  return rowToDoc(updated as Record<string, unknown>);
}

// ── Classification ───────────────────────────────────────

export async function classifyDocument(
  docRecordId: number,
  responsiveClass: ResponsiveClass,
  exemptionCodes: string[],
  actorEmail: string,
): Promise<FoiaDocument> {
  const updated = await updateRecord(TABLES.documents, docRecordId, {
    [F.responsiveClass]: responsiveClass,
    [F.isResponsive]:    responsiveClass === 'responsive' || responsiveClass === 'partiallyResponsive',
    [F.exemptionCodes]:  JSON.stringify(exemptionCodes),
  });

  // Audit
  try {
    await createRecord(TABLES.auditLog, {
      [FIELDS.auditLog.action]:    'document.classified',
      [FIELDS.auditLog.userEmail]: actorEmail,
      [FIELDS.auditLog.details]:   JSON.stringify({ docRecordId, responsiveClass, exemptionCodes }),
      [FIELDS.auditLog.timestamp]: new Date().toISOString(),
    });
  } catch { /* don't block */ }

  return rowToDoc(updated as Record<string, unknown>);
}

// ── Release ──────────────────────────────────────────────

/**
 * Mark a document as released to the requester.
 * After calling this, getDownloadUrlForRelease() will return a URL.
 *
 * Prerequisites (enforced by caller):
 *   - Document must be Responsive or Partially Responsive
 *   - If partially responsive: redactionStatus must be 'approved'
 *   - Supervisor approval must have been obtained
 */
export async function releaseDocument(
  docRecordId: number,
  actorEmail: string,
): Promise<FoiaDocument> {
  const now = new Date().toISOString();
  const updated = await updateRecord(TABLES.documents, docRecordId, {
    [F.isReleased]:  true,
    [F.releasedAt]:  now,
  });

  try {
    await createRecord(TABLES.auditLog, {
      [FIELDS.auditLog.action]:    'document.released',
      [FIELDS.auditLog.userEmail]: actorEmail,
      [FIELDS.auditLog.details]:   JSON.stringify({ docRecordId }),
      [FIELDS.auditLog.timestamp]: now,
    });
  } catch { /* don't block */ }

  return rowToDoc(updated as Record<string, unknown>);
}

export async function unreleaseDocument(
  docRecordId: number,
  actorEmail: string,
): Promise<FoiaDocument> {
  const now = new Date().toISOString();
  const updated = await updateRecord(TABLES.documents, docRecordId, {
    [F.isReleased]: false,
    [F.releasedAt]: '',
  });

  try {
    await createRecord(TABLES.auditLog, {
      [FIELDS.auditLog.action]:    'document.unreleased',
      [FIELDS.auditLog.userEmail]: actorEmail,
      [FIELDS.auditLog.details]:   JSON.stringify({ docRecordId }),
      [FIELDS.auditLog.timestamp]: now,
    });
  } catch { /* don't block */ }

  return rowToDoc(updated as Record<string, unknown>);
}

/**
 * Get a signed download URL for a released document.
 * Always serves the redacted version when one exists, regardless of classification.
 * Falls back to the original only if no redacted copy has been burned.
 * Always verifies isReleased before generating the URL.
 *
 * Returns null if:
 *   - Document is not marked released
 *   - No file exists (neither original nor redacted)
 */
export async function getDownloadUrlForRelease(
  doc: FoiaDocument,
  opts?: { anonymous?: boolean },
): Promise<string | null> {
  if (!doc.isReleased) return null;

  // Always serve the redacted version when one exists
  if (doc.redactedStorageUrl) {
    return getDownloadUrlFromField(doc.redactedStorageUrl, opts);
  }

  return getDownloadUrlFromField(doc.storageUrl, opts);
}

/**
 * Get a signed URL for a staff member to download a document.
 * Prefers the redacted version when one exists (so staff can verify the output).
 * Falls back to the original. Never call this for public/anonymous access.
 */
export async function getStaffDownloadUrl(doc: FoiaDocument): Promise<string | null> {
  if (doc.redactedStorageUrl) {
    return getDownloadUrlFromField(doc.redactedStorageUrl, { anonymous: false });
  }
  return getDownloadUrlFromField(doc.storageUrl, { anonymous: false });
}

/**
 * Get a signed URL for the original (unredacted) file regardless of redaction status.
 * Use this when staff explicitly needs to view the source document.
 */
export async function getOriginalDownloadUrl(doc: FoiaDocument): Promise<string | null> {
  return getDownloadUrlFromField(doc.storageUrl, { anonymous: false });
}

export async function deleteFoiaDocument(recordId: number): Promise<void> {
  await deleteRecord(TABLES.documents, recordId);
}

// ── Public Records Library ───────────────────────────────

export async function publishDocument(
  doc: FoiaDocument,
  actorEmail: string,
): Promise<FoiaDocument> {
  if (doc.isPublished) return doc;
  const newNotes = doc.notes ? `[PUBLISHED] ${doc.notes}` : '[PUBLISHED]';
  const updated = await updateRecord(TABLES.documents, doc.recordId, { [F.notes]: newNotes });
  try {
    await createRecord(TABLES.auditLog, {
      [FIELDS.auditLog.action]:    'document.published',
      [FIELDS.auditLog.userEmail]: actorEmail,
      [FIELDS.auditLog.details]:   JSON.stringify({ docRecordId: doc.recordId }),
      [FIELDS.auditLog.timestamp]: new Date().toISOString(),
    });
  } catch { /* don't block */ }
  return rowToDoc(updated as Record<string, unknown>);
}

export async function unpublishDocument(
  doc: FoiaDocument,
  actorEmail: string,
): Promise<FoiaDocument> {
  if (!doc.isPublished) return doc;
  const newNotes = (doc.notes ?? '').replace('[PUBLISHED]', '').trim();
  const updated = await updateRecord(TABLES.documents, doc.recordId, { [F.notes]: newNotes });
  try {
    await createRecord(TABLES.auditLog, {
      [FIELDS.auditLog.action]:    'document.unpublished',
      [FIELDS.auditLog.userEmail]: actorEmail,
      [FIELDS.auditLog.details]:   JSON.stringify({ docRecordId: doc.recordId }),
      [FIELDS.auditLog.timestamp]: new Date().toISOString(),
    });
  } catch { /* don't block */ }
  return rowToDoc(updated as Record<string, unknown>);
}

export async function listPublishedDocuments(opts?: { anonymous?: boolean }): Promise<FoiaDocument[]> {
  const { records } = await queryRecords(TABLES.documents, {
    filters: [
      { field: F.isReleased, operator: 'eq', value: true },
      { field: F.notes, operator: 'contains', value: '[PUBLISHED]' },
    ],
    sort: { field: F.releasedAt, direction: 'desc' },
    limit: 500,
  }, opts);
  return (records as Record<string, unknown>[]).map(rowToDoc);
}

